/**
 * Sentry-Init fuer das React-Portal (Phase 7a, 2026-05-08).
 *
 * Wird vor `createRoot()` in main.tsx aufgerufen. Liest die DSN aus dem
 * bei Build-Time injecten Vite-Env (VITE_SENTRY_DSN). Ohne DSN: no-op.
 *
 * Best-Effort-Init — wenn import oder init fehlschlaegt, soll die App trotzdem
 * starten (kein harter Sentry-Fail).
 */

export async function initSentry(): Promise<void> {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim()
  if (!dsn) return
  try {
    const Sentry = await import("@sentry/react")
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_DEPLOY_ENV as string | undefined) || "prod",
      release: (import.meta.env.VITE_RELEASE_VERSION as string | undefined) || "dev",
      // Browser-Tracing default: Performance-Sampling 5%
      tracesSampleRate: 0.05,
      // Session-Replay deaktiviert — nimmt zu viel auf, DSGVO-sensitiv
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Ignoriert die typischen Browser-Noise-Errors
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
        "ChunkLoadError", // typisch nach Deploy mit alter Bundle-Version
      ],
      // Auf 422 / 409 Antworten kein Sentry — die sind erwartet (Sanity-Checks)
      beforeSend(event) {
        const status = (event.contexts as Record<string, unknown> | undefined)?.response
        if (typeof status === "object" && status && "status_code" in status) {
          const code = (status as { status_code?: number }).status_code
          if (code === 422 || code === 409) return null
        }
        return event
      },
    })
  } catch (e) {
    console.warn("[sentry] init failed (continuing without):", e)
  }
}
